import { canv } from "@/canv/global";
import { getViewport } from "@/canvasController/helpers";
import type { Group } from "@/group";

type Grid = Map<string, Set<Group>>;
type CellByUID = Map<string, string[]>;
type Params = Pick<Group, "startX" | "startY" | "width" | "height">;
export class HashGrid {
    constructor(cellSize = 750) {
        this.cellSize = cellSize;
        this.grid = new Map();
        this.cellByUID = new Map();
    }

    getCellKey(x: number, y: number) {
        const keyX = Math.floor(x / this.cellSize);
        const keyY = Math.floor(y / this.cellSize);

        return `${keyX},${keyY}`;
    }

    getCellsForElement(params: Params) {
        const viewPort = getViewport();

        const startX = Math.max(params.startX, viewPort.viewportStartX);
        const startY = Math.max(params.startY, viewPort.viewportStartY);
        const endX = Math.min(
            params.startX + params.width,
            viewPort.viewportStartX + viewPort.viewportWidth,
        );
        const endY = Math.min(
            params.startY + params.height,
            viewPort.viewportStartY + viewPort.viewportHeight,
        );

        const startXCell = Math.floor(startX / this.cellSize);
        const startYCell = Math.floor(startY / this.cellSize);
        const endXCell = Math.floor(endX / this.cellSize);
        const endYCell = Math.floor(endY / this.cellSize);

        const cells: string[] = [];

        for (let x = startXCell; x <= endXCell; x++) {
            for (let y = startYCell; y <= endYCell; y++) {
                cells.push(`${x},${y}`);
            }
        }

        return cells;
    }

    needToUpdate(element: Group) {
        const oldCells = this.cellByUID.get(element.UID) || [];
        const cells = this.getCellsForElement(element);

        if (oldCells.length !== cells.length) {
            return true;
        }

        return cells.some((cell) => !oldCells.includes(cell));
    }

    insert(element: Group) {
        const cells = this.getCellsForElement(element);

        cells.forEach((cell) => {
            if (!this.grid.has(cell)) {
                this.grid.set(cell, new Set());
            }

            this.grid.get(cell)?.add(element);
        });

        this.cellByUID.set(element.UID, cells);
    }

    query(x: number, y: number) {
        const cell = this.getCellKey(x, y);

        if (this.grid.has(cell)) {
            const gridCell = this.grid.get(cell);
            if (gridCell) {
                return Array.from(gridCell);
            }
        }

        return [];
    }

    queryByViewport() {
        const viewport = getViewport();

        return this.queryArea({
            startX: viewport.viewportStartX,
            startY: viewport.viewportStartY,
            width: viewport.viewportWidth,
            height: viewport.viewportHeight,
        });
    }

    queryArea(area: Params): Group[] {
        const areaCells = this.getCellsForElement(area);

        const found: Set<Group> = new Set();

        for (const cell of areaCells) {
            if (this.grid.has(cell)) {
                const temp = this.grid.get(cell);
                if (!temp) {
                    continue;
                }
                for (const value of temp) {
                    found.add(value);
                }
            }
        }

        return Array.from(found);
    }

    update(element: Group) {
        const oldCells = this.cellByUID.get(element.UID);

        this.remove(element, oldCells);
        this.insert(element);
    }

    remove(element: Group, cellsProps?: string[]) {
        if (!this.cellByUID.has(element.UID)) return;

        const cells = cellsProps || this.getCellsForElement(element);

        cells?.forEach((cell) => {
            if (this.grid.has(cell)) {
                const gridCell = this.grid.get(cell);
                if (!gridCell) {
                    return;
                }
                gridCell.delete(element);

                if (!gridCell.size) {
                    this.grid.delete(cell);
                }
            }
        });

        this.cellByUID.delete(element.UID);
    }

    cellSize: number;
    grid: Grid;
    cellByUID: CellByUID;
}
